/**
 * Profil-Selbstzuordnung (Phase 7.7 — Google-Workspace-SSO).
 *
 * Vollbild im Login-Stil. Ein frisch via Workspace-SSO eingeloggter User, dessen
 * Adresse noch keinem CT-Profil zugeordnet ist, wählt hier sein Profil aus. Die
 * Adresse wird dem Profil dann fest zugewiesen (Email_Alt). Profile mit erhöhten
 * Rechten (requires_approval) gehen statt direkt zu „pending" → Admin-Freigabe
 * (siehe /admin/zugriffsanfragen).
 *
 * Alle Ergebnis-States (linked / pending / error / empty) werden rein clientseitig
 * im selben host neu gerendert.
 */

import { claimSubmit, ApiError } from "../api/client";
import type { ClaimProfile, ClaimProfilesResult } from "../api/client";
import { clearToken } from "../auth/token";
import { avatarHtml, hydrateAvatars } from "../components/avatar";
import { icon } from "../lib/icons";
import "../styles/auth-claim.css";
import { escapeHtml, escapeAttr } from "../lib/html";

const REASON_LABEL: Record<string, string> = {
  sensitive_role: "Erhöhte Rechte",
  alt_email_occupied: "Alt-Mail belegt"
};

export function renderAuthClaimProfile(host: HTMLElement, data: ClaimProfilesResult): void {
  if (data.already_linked || data.profiles.length === 0) {
    renderEmpty(host, data.email);
    return;
  }
  renderChooser(host, data);
}

// =============================================================================
// Auswahl-State
// =============================================================================

function renderChooser(host: HTMLElement, data: ClaimProfilesResult): void {
  const showSearch = data.profiles.length > 4;
  host.innerHTML = `
    <div class="login-page">
      <div class="login-card claim-card">
        <div class="login-brand">
          <img src="/logo.png" alt="CoTrainer" />
          <h1>CoTrainer Controlling</h1>
        </div>
        <div class="claim-head">
          <h2>Profil zuordnen</h2>
          <p class="claim-sub">Angemeldet als <code class="claim-mail">${escapeHtml(data.email)}</code>. Wähle dein Profil — deine Adresse wird ihm dann fest zugewiesen.</p>
        </div>
        ${showSearch ? `<input type="text" class="claim-search" id="claim-search" placeholder="Profil suchen…" autocomplete="off" />` : ""}
        <div class="claim-list" id="claim-list">${profilesHtml(data.profiles)}</div>
        <div class="claim-actions">
          <button type="button" class="login-btn" id="claim-submit" disabled><span>Zuordnen</span></button>
          <button type="button" class="claim-footlink" id="claim-logout">Abmelden / anderes Konto</button>
        </div>
      </div>
    </div>`;

  void hydrateAvatars(host);

  const list = host.querySelector<HTMLElement>("#claim-list")!;
  const submitBtn = host.querySelector<HTMLButtonElement>("#claim-submit")!;

  const syncSubmit = () => {
    submitBtn.disabled = !host.querySelector<HTMLInputElement>('input[name="claim-profile"]:checked');
  };
  list.addEventListener("change", syncSubmit);

  const search = host.querySelector<HTMLInputElement>("#claim-search");
  search?.addEventListener("input", () => {
    const q = search.value.trim().toLowerCase();
    list.querySelectorAll<HTMLElement>(".claim-option").forEach((opt) => {
      const hay = (opt.dataset.search ?? "").toLowerCase();
      opt.style.display = !q || hay.includes(q) ? "" : "none";
    });
  });

  submitBtn.addEventListener("click", () => {
    const picked = host.querySelector<HTMLInputElement>('input[name="claim-profile"]:checked');
    if (!picked) return;
    void onSubmit(host, data, picked.value);
  });

  host.querySelector("#claim-logout")?.addEventListener("click", () => {
    clearToken();
    window.location.href = "/login";
  });
}

function profilesHtml(profiles: ClaimProfile[]): string {
  return profiles.map((p) => {
    const av = avatarHtml({ vollname: p.vollname, farbe: colorFor(p.person_id), personId: p.person_id, size: 36 });
    const pill = p.requires_approval
      ? `<span class="claim-pill" title="Dieses Profil hat erhöhte Rechte und muss von einem Admin freigegeben werden.">${icon("lock", 13)}Freigabe nötig</span>`
      : "";
    const search = `${p.vollname} ${p.person_id}`;
    return `
      <label class="claim-option" data-search="${escapeAttr(search)}">
        <input type="radio" name="claim-profile" value="${escapeAttr(p.person_id)}" />
        ${av}
        <span class="claim-option-body">
          <span class="claim-option-name">${escapeHtml(p.vollname)}</span>
          <span class="claim-option-id"><code>${escapeHtml(p.person_id)}</code></span>
        </span>
        ${pill}
      </label>`;
  }).join("");
}

// =============================================================================
// Submit + Ergebnis-States
// =============================================================================

async function onSubmit(host: HTMLElement, data: ClaimProfilesResult, personId: string): Promise<void> {
  const submitBtn = host.querySelector<HTMLButtonElement>("#claim-submit");
  if (submitBtn) submitBtn.disabled = true;
  try {
    const res = await claimSubmit(personId);
    if (res.status === "linked" || res.status === "already_linked") {
      renderLinked(host, res.vollname);
    } else {
      renderPending(host, res.reason, res.claim_id);
    }
  } catch (err) {
    renderError(host, data, err);
  }
}

function renderLinked(host: HTMLElement, vollname?: string): void {
  host.innerHTML = shell(`
    <div class="claim-result">
      <div class="claim-circle is-ok">${icon("check-circle", 32)}</div>
      <h2>Profil verknüpft</h2>
      <p class="claim-result-text">${vollname ? escapeHtml(vollname) + " ist jetzt dir zugeordnet." : "Dein Profil ist jetzt zugeordnet."}</p>
      <div class="claim-progress"><span></span></div>
    </div>`);
  window.setTimeout(() => { window.location.href = "/"; }, 800);
}

function renderPending(host: HTMLElement, reason?: string, claimId?: string): void {
  const reasonPill = reason && REASON_LABEL[reason]
    ? `<span class="claim-reason-pill">${escapeHtml(REASON_LABEL[reason]!)}</span>`
    : `<span class="claim-reason-pill">Freigabe nötig</span>`;
  const claimRow = claimId
    ? `<div class="claim-info-row"><span class="claim-info-label">Anfrage-ID</span><code>${escapeHtml(claimId)}</code></div>`
    : "";
  host.innerHTML = shell(`
    <div class="claim-result">
      <div class="claim-circle is-pending">${icon("clock", 32)}</div>
      <h2>Anfrage gesendet</h2>
      <p class="claim-result-text">Deine Zuordnung muss von einem Admin freigegeben werden.</p>
      <div class="claim-info-box">
        ${claimRow}
        <div class="claim-info-row"><span class="claim-info-label">Grund</span>${reasonPill}</div>
        <div class="claim-info-row"><span class="claim-info-label">Status</span><span class="claim-info-label">wartet auf Admin</span></div>
      </div>
      <p class="claim-empty-hint">Du kannst dieses Fenster schließen — wir melden uns, sobald freigegeben.</p>
    </div>`);
}

function renderError(host: HTMLElement, data: ClaimProfilesResult, err: unknown): void {
  const code = err instanceof ApiError ? err.code : "EXCEPTION";
  const message = err instanceof Error ? err.message : String(err);
  host.innerHTML = shell(`
    <div class="claim-result">
      <div class="claim-circle is-error">${icon("x", 32)}</div>
      <h2>Zuordnung fehlgeschlagen</h2>
      <div class="claim-error-box">
        <div><code>${escapeHtml(code)}</code></div>
        <div>${escapeHtml(message)}</div>
      </div>
      <button type="button" class="login-btn" id="claim-back"><span>Zurück zur Auswahl</span></button>
    </div>`);
  host.querySelector("#claim-back")?.addEventListener("click", () => renderChooser(host, data));
}

function renderEmpty(host: HTMLElement, email: string): void {
  host.innerHTML = shell(`
    <div class="claim-result">
      <div class="claim-circle is-neutral">${icon("user-x", 32)}</div>
      <h2>Kein Profil gefunden</h2>
      <p class="claim-result-text">Für <code class="claim-mail">${escapeHtml(email)}</code> ist kein zuordenbares Profil hinterlegt.</p>
      <p class="claim-empty-hint">Bitte wende dich an einen Admin, um dein Profil anlegen oder freischalten zu lassen.</p>
      <button type="button" class="claim-footlink" id="claim-logout">Abmelden / anderes Konto</button>
    </div>`);
  host.querySelector("#claim-logout")?.addEventListener("click", () => {
    clearToken();
    window.location.href = "/login";
  });
}

/** Hüllt einen Ergebnis-Block in die Login-Card-Shell (mit Brand). */
function shell(inner: string): string {
  return `
    <div class="login-page">
      <div class="login-card claim-card">
        <div class="login-brand">
          <img src="/logo.png" alt="CoTrainer" />
          <h1>CoTrainer Controlling</h1>
        </div>
        ${inner}
      </div>
    </div>`;
}

// =============================================================================
// Helpers
// =============================================================================

/** Deterministische Avatar-Farbe aus der person_id (HSL → Hex-frei via hsl()). */
function colorFor(personId: string): string {
  let h = 0;
  for (let i = 0; i < personId.length; i++) {
    h = (h * 31 + personId.charCodeAt(i)) % 360;
  }
  return `hsl(${h}, 42%, 46%)`;
}
